import { doMutation } from "../../core-module/util/graphqlHelper";
import { apolloClient } from "@/plugins/vue-apollo";
import {
  filterEmptyLocalisedValues,
  filterEmptyMediaUrls,
  getInteger,
} from "@/app/core-module/util/coreHelper";
import { getTrainingModuleContentInput } from "@/app/course-module/methods/createOrUpdateTrainingModuleContent";
import { getTrainingModuleInput } from "@/app/course-module/methods/createOrUpdateTrainingModule";

const updateGetTrainingsCache = ({ trainingId, training, variables }) => {
  try {
    if (trainingId && training) {
      const cacheKey = {
        query: require("../api/query/getTrainings.gql"),
        variables: variables,
      };

      const cachedData = apolloClient.readQuery(cacheKey);

      if (cachedData?.getTrainings) {
        const existingTrainingIndex =
          cachedData.getTrainings.trainings.findIndex(
            x => x._id === training._id
          );

        if (existingTrainingIndex >= 0) {
          cachedData.getTrainings.trainings[existingTrainingIndex] = training;
        } else {
          cachedData.getTrainings.trainings.unshift(training);
        }

        apolloClient.writeQuery({ ...cacheKey, data: cachedData });
      }
    }
  } catch (e) {
    // console.log(
    //   "createOrUpdateTraining.updateGetTrainingsCache:exception:",
    //   e.message
    // );
  }
};

const getTrainingInput = training => {
  const input = { _id: training._id };

  if (training.trainingCategories) {
    input.trainingCategories = training.trainingCategories.map(x => x._id);
  }

  if (training.trainingSubCategories) {
    input.trainingSubCategories = training.trainingSubCategories.map(
      x => x._id
    );
  }

  if (training.title) {
    const localisedTitles = filterEmptyLocalisedValues(training.title) ?? [];

    if (localisedTitles.length) input.title = localisedTitles;
  }

  if (training.description) {
    input.description = filterEmptyLocalisedValues(training.description) ?? [];
  }

  if (training.instructions) {
    input.instructions =
      filterEmptyLocalisedValues(training.instructions) ?? [];
  }

  if (training.feedback) {
    if (training.feedback?.questionAnswers?.length) {
      training.feedback.questionAnswers = training.feedback.questionAnswers.map(
        (questionAnswer, questionAnswerIndex) => {
          return {
            _id: questionAnswer._id,
            question: filterEmptyLocalisedValues(questionAnswer.question) ?? [],
            choices: questionAnswer.choices?.map(choice => {
              return {
                key: choice.key,
                value: filterEmptyLocalisedValues(choice.value) ?? [],
              };
            }),
            isDescriptive: questionAnswer.isDescriptive,
            displayPosition: questionAnswerIndex, //questionAnswer.displayPosition,//removed for dragging,
          };
        }
      );

      input.feedback = {
        questionAnswers: training.feedback.questionAnswers,
      };
    }
  }

  if (training.feedbackContent) {
    input.feedbackContent = training.feedbackContent;
  }

  if (training.trainingImages) {
    const images = filterEmptyMediaUrls(training.trainingImages);

    if (images.length) input.images = images;
  }

  if (training.price) {
    const price = (() => {
      try {
        return parseFloat(training.price);
      } catch (_) {}
    })();

    if (price) input.price = price;
  }

  if (training.duration) {
    const duration = getInteger(training.duration);
    if (duration) input.duration = duration;
  }

  if (training.certificateValidity) {
    const certificateValidity = getInteger(training.certificateValidity);
    if (certificateValidity) input.certificateValidity = certificateValidity;
  }

  if (training.trainingModules?.length) {
    const trainingModules = [];

    training.trainingModules.forEach((trainingModule, trainingModuleIndex) => {
      const trainingModuleInput = {
        ...getTrainingModuleInput(trainingModule),
        trainingModuleContents: [],
      };

      if (trainingModule.trainingModuleContents?.length) {
        trainingModule.trainingModuleContents.forEach(
          (trainingModuleContent, trainingModuleContentIndex) => {
            const trainingModuleContentInput = getTrainingModuleContentInput(
              trainingModuleContent
            );

            trainingModuleInput.trainingModuleContents.push({
              ...trainingModuleContentInput,
              displayPosition: trainingModuleContentIndex,
            });
          }
        );
      }

      trainingModules.push({
        ...trainingModuleInput,
        displayPosition: trainingModuleIndex,
      });
    });

    if (trainingModules.length) input.trainingModules = trainingModules;
  }

  if (training.deletedTrainingModules?.length) {
    const deletedTrainingModules = training.deletedTrainingModules.filter(
      x => x != null
    );

    if (deletedTrainingModules.length)
      input.deletedTrainingModules = deletedTrainingModules;
  }

  if (training.deletedTrainingModuleContents?.length) {
    const deletedTrainingModuleContents =
      training.deletedTrainingModuleContents.filter(x => x != null);

    if (deletedTrainingModuleContents.length)
      input.deletedTrainingModuleContents = deletedTrainingModuleContents;
  }

  return input;
};

export const createOrUpdateTraining = training => {
  const mutation = require("../api/mutation/createOrUpdateTraining.gql");
  const variables = { input: getTrainingInput(training) };

  return doMutation({ mutation, variables }).then(({ data, error }) => {
    if (data) {
      updateGetTrainingsCache({
        trainingId: data?.createOrUpdateTraining?._id,
        training: data?.createOrUpdateTraining,
        variables: {
          limit: 0,
          skip: 0,
        },
      });
      return { data: data.createOrUpdateTraining };
    } else if (error) return { error: JSON.stringify(error) };
  });
};
